import {
  Badge,
  Col,
  DateRangePicker,
  Loading,
  Row,
  cn,
  humanReadableDateTime,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronLeft, LucideChevronRight } from 'lucide-react';

import { outboxEventStatusColor } from './enumLabels';

import { AssistantOutboxEventRecord } from '@/audit-log/_graphql/queryAssistantOutboxEvents';
import {
  $remoteAssistantOutboxEvents,
  useAssistantDateRangeFilter,
  useAssistantPagination,
  useSelectedAssistantEventId,
} from '@/audit-log/_state/assistantEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

type EventTypeTranslations = {
  chat: {
    created: string;
    updated: string;
    archived: string;
    titleUpdated: string;
  };
  chatMessage: {
    created: string;
  };
};

const eventTypeByEventName: Record<string, keyof EventTypeTranslations> = {
  ChatEvent: 'chat',
  ChatMessageEvent: 'chatMessage',
};

const ACTIONS = ['created', 'updated', 'archived', 'titleUpdated'] as const;

const getEventTypeKey = (
  eventName: string,
  payload: string,
): {
  aggregate: keyof EventTypeTranslations;
  action: (typeof ACTIONS)[number];
} | null => {
  const aggregate = eventTypeByEventName[eventName];
  if (!aggregate) return null;

  try {
    const parsed = JSON.parse(payload);
    const action = ACTIONS.find((a) => parsed[a]);
    if (!action) return null;

    return { aggregate, action };
  } catch {
    return null;
  }
};

const getTranslatedEventName = (
  eventName: string,
  payload: string,
  eventTypes: EventTypeTranslations,
): string => {
  const key = getEventTypeKey(eventName, payload);
  if (key) {
    const aggregateTranslations = eventTypes[key.aggregate];
    if (key.action in aggregateTranslations) {
      return aggregateTranslations[
        key.action as keyof typeof aggregateTranslations
      ];
    }
  }
  return eventName;
};

type EventCardProps = {
  event: AssistantOutboxEventRecord;
  isSelected: boolean;
  onClick: () => void;
  idPrefix: string;
  notAvailable: string;
  eventTypes: EventTypeTranslations;
};

//
// ⚛️ ⚛️ ⚛️ EVENT CARD ⚛️ ⚛️ ⚛️
//
const EventCard = ({
  event,
  isSelected,
  onClick,
  idPrefix,
  notAvailable,
  eventTypes,
}: EventCardProps) => (
  <button
    onClick={onClick}
    className={cn(
      '@EventCard',
      'w-full rounded-md border p-3 text-left transition-colors',
      isSelected && 'border-primary bg-primary/5',
      !isSelected &&
        'border-neutral/10 hover:border-neutral/20 hover:bg-neutral/5',
    )}
  >
    <Col gap2>
      <Row xBetween>
        <span className="text-sm font-medium">
          {getTranslatedEventName(event.eventName, event.payload, eventTypes)}
        </span>
        <Badge color={outboxEventStatusColor[event.status]} size="small">
          {event.status}
        </Badge>
      </Row>
      <Row className="text-xs text-neutral/60" gap2>
        <span>
          {idPrefix} {event.metadata.aggregateId ?? notAvailable}
        </span>
      </Row>
      <Row className="text-xs text-neutral/40">
        <span>
          {humanReadableDateTime(event.timestamps.createdAt, {
            locale: getLocale(),
            includeYear: true,
            includeSeconds: true,
          })}
        </span>
      </Row>
    </Col>
  </button>
);

//
// ⚛️ ⚛️ ⚛️ ASSISTANT EVENTS LIST ⚛️ ⚛️ ⚛️
//
export const AssistantEventsList = () => {
  const t = useTranslations();

  // Atoms
  const { data, isLoading } = useAtomValue($remoteAssistantOutboxEvents);
  const [pagination, setPagination] = useAssistantPagination();
  const [selectedEventId, setSelectedEventId] = useSelectedAssistantEventId();
  const [dateRange, setDateRange] = useAssistantDateRangeFilter();

  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  const hasNextPage = pagination.offset + pagination.limit < total;
  const hasPrevPage = pagination.offset > 0;

  // Callbacks
  const goToNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const goToPrevPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  if (isLoading && !data) {
    return (
      <Col className="@AssistantEventsList flex-1 items-center justify-center border-r border-neutral/10">
        <Loading isLoading />
      </Col>
    );
  }

  return (
    <Col className="@AssistantEventsList w-[350px] border-r border-neutral/10">
      <Row className="border-b border-neutral/10 p-3">
        <Col className="flex-1">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            aria-label={t.filters.dateRange.label}
            clearable
            onClear={() => setDateRange(null)}
          />
        </Col>
      </Row>
      <Col className="flex-1 gap-2 overflow-y-auto p-3">
        {events.length === 0 ? (
          <Col className="flex-1 items-center justify-center text-neutral/40">
            <span>{t.assistantEventsView.list.empty}</span>
          </Col>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isSelected={selectedEventId === event.id}
              onClick={() => setSelectedEventId(event.id)}
              idPrefix={t.assistantEventsView.list.idPrefix}
              notAvailable={t.assistantEventsView.list.notAvailable}
              eventTypes={t.assistantEventsView.detail.eventTypes}
            />
          ))
        )}
      </Col>

      <Row className="items-center justify-between border-t border-neutral/10 px-3 py-2">
        <span className="text-xs text-neutral/60">
          {pagination.offset + 1} -{' '}
          {Math.min(pagination.offset + pagination.limit, total)} of {total}
        </span>
        <Row gap1>
          <button
            onClick={goToPrevPage}
            disabled={!hasPrevPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasPrevPage && 'hover:bg-neutral/10',
              !hasPrevPage && 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronLeft size={16} />
          </button>
          <button
            onClick={goToNextPage}
            disabled={!hasNextPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasNextPage && 'hover:bg-neutral/10',
              !hasNextPage && 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronRight size={16} />
          </button>
        </Row>
      </Row>
    </Col>
  );
};
